'use client';

/**
 * Count-up for authored stat values ("3.000.000+", "%95", "1,5 milyon").
 *
 * <p>The value stays free text — authors type it the way their language
 * writes numbers — so the first number in it is parsed, animated from 0,
 * and re-printed with the source's own thousands and decimal separators
 * between the untouched prefix and suffix. Text without a number renders
 * as typed.
 *
 * <p>The box is sized by an invisible copy of the final value, so the
 * digits growing never widen it and never shove the neighbouring stats
 * sideways. The final value is also what assistive tech reads; the ticking
 * digits are hidden from it.
 */
import { useEffect, useMemo, useRef, useState } from 'react';

export interface ParsedStatNumber {
  prefix: string;
  suffix: string;
  value: number;
  decimals: number;
  /** Thousands separator as written ("" when the source had none). */
  group: string;
  /** Decimal separator as written ("" for whole numbers). */
  decimal: string;
}

/** First number in `text`, with the separators it was written with. */
export function parseStatNumber(text: string): ParsedStatNumber | null {
  const match = /\d[\d.,'\u00A0\u202F ]*/.exec(text);
  if (!match) return null;
  // Separators after the last digit belong to the suffix ("14. sırada").
  const run = match[0].replace(/[^\d]+$/, '');
  const prefix = text.slice(0, match.index);
  const suffix = text.slice(match.index + run.length);

  const separators = run.match(/\D/g) ?? [];
  let intPart = run;
  let fracPart = '';
  let group = '';
  let decimal = '';
  if (separators.length > 0) {
    const last = separators[separators.length - 1] as string;
    const kinds = new Set(separators);
    const lastAt = run.lastIndexOf(last);
    const tail = run.slice(lastAt + 1);
    // Two kinds ("1,234.5") → the last is the decimal point. One kind used
    // once with a tail that isn't three digits ("2,5") → decimal too.
    // Anything else ("3.000.000", "1.500") → thousands grouping.
    const isDecimal = kinds.size > 1 || (separators.length === 1 && tail.length !== 3);
    if (isDecimal) {
      decimal = last;
      fracPart = tail;
      intPart = run.slice(0, lastAt);
      group = [...kinds].find((k) => k !== last) ?? '';
    } else {
      group = last;
    }
  }

  const value = Number(`${intPart.replace(/\D/g, '')}${fracPart ? `.${fracPart}` : ''}`);
  if (!Number.isFinite(value)) return null;
  return { prefix, suffix, value, decimals: fracPart.length, group, decimal };
}

export function formatStatNumber(parsed: ParsedStatNumber, n: number): string {
  const [int = '0', frac] = n.toFixed(parsed.decimals).split('.');
  const grouped = parsed.group ? int.replace(/\B(?=(\d{3})+(?!\d))/g, parsed.group) : int;
  return `${parsed.prefix}${grouped}${frac ? `${parsed.decimal}${frac}` : ''}${parsed.suffix}`;
}

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

const srOnly = {
  position: 'absolute',
  width: 1,
  height: 1,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
} as const;

/**
 * Renders `text`, counting its number up from 0 the first time it is on
 * screen while `active`. Reduced motion, `enabled={false}` or text without
 * a number show the final value straight away.
 */
export function CountUpText({
  text,
  enabled = true,
  active = true,
  duration = 2000,
}: {
  text: string;
  enabled?: boolean;
  /** False while the slide carrying it is off stage — don't spend the
   *  animation where nobody can see it. */
  active?: boolean;
  duration?: number;
}) {
  const parsed = useMemo(() => parseStatNumber(text), [text]);
  const animates = Boolean(enabled && parsed && parsed.value > 0);
  const ref = useRef<HTMLSpanElement>(null);
  const done = useRef(false);
  // Server and first client render agree on 0, so hydration never mismatches.
  const [progress, setProgress] = useState(animates ? 0 : 1);

  useEffect(() => {
    if (!animates) {
      setProgress(1);
      return;
    }
    if (done.current || !active) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      done.current = true;
      setProgress(1);
      return;
    }

    let frame = 0;
    let started = false;
    const run = () => {
      started = true;
      done.current = true;
      const start = performance.now();
      const ms = Math.max(200, Number(duration) || 2000);
      const tick = (now: number) => {
        const t = Math.min(1, (now - start) / ms);
        setProgress(easeOutCubic(t));
        if (t < 1) frame = requestAnimationFrame(tick);
      };
      frame = requestAnimationFrame(tick);
    };

    // Cut short (slide changed, props edited) → settle on the final value
    // rather than freezing mid-count; it won't run again.
    const stop = () => {
      cancelAnimationFrame(frame);
      if (started) setProgress(1);
    };

    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      run();
      return stop;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        observer.disconnect();
        run();
      },
      { threshold: 0.4 },
    );
    observer.observe(el);
    return () => {
      observer.disconnect();
      stop();
    };
  }, [animates, active, duration]);

  if (!parsed || !animates) return <>{text}</>;

  const shown = progress >= 1 ? text : formatStatNumber(parsed, parsed.value * progress);
  return (
    <span ref={ref} style={{ position: 'relative', display: 'inline-grid' }}>
      <span aria-hidden="true" style={{ gridArea: '1 / 1', visibility: 'hidden' }}>
        {text}
      </span>
      <span aria-hidden="true" style={{ gridArea: '1 / 1' }}>
        {shown}
      </span>
      <span style={srOnly}>{text}</span>
    </span>
  );
}
